import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FaChevronLeft, FaSpinner } from 'react-icons/fa';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import ChatPage from '../chat/ChatPage';

import { ChatbotThemeGradients } from '@/components/utils/constants';
import {
	formatPrice,
	getMatchedVariant,
	getThemeValue,
} from '@/components/utils/utils';
import { useHaggleReducer } from '@/hooks/useHaggleReducer';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { IProduct } from '@/interfaces/api/product/IProduct';
import { haggleApiService } from '@/services/haggle.service';

const ShopifyProductDetail = () => {
	const { state } = useLocation();
	const { vendorEmail } = state || {};
	const navigate = useNavigate();

	const { productId, storeName } = useParams<{
		productId: string;
		storeName: string;
	}>();

	const [haggleReducerState, dispatch] = useHaggleReducer();
	const {
		product,
		selectedImage,
		isProductInHaggle,
		selectedOptions,
		selectedVariant,
		isChatOpen,
		chatKey,
		isChatMinimized,
		isChatInProgress,
		customReturnAndExchangePolicy,
		shippingPolicy,
	} = haggleReducerState;
	const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
	const { data: store, refetch: refetchStore } = useQuery({
		queryKey: [ApiQueryKeys.getStoreThemeByName, storeName],
		queryFn: () => haggleApiService.getStoreThemeByName(storeName),
		enabled: !!storeName,
	});
	const initializeOptionsAndVariant = (product: IProduct) => {
		const initialOptions: Record<string, string> = {};
		product.options.forEach((option, index) => {
			if (option.values.length > 0) {
				initialOptions[`option${index + 1}`] = option.values[0];
			}
		});
		const matched = getMatchedVariant(product.variants, initialOptions);

		dispatch({ type: 'UPDATE_OPTIONS', payload: initialOptions });
		dispatch({ type: 'SET_VARIANT', payload: matched || product.variants[0] });
	};

	const selectVariant = (value: string, position: number) => {
		const updatedOptions = {
			...selectedOptions,
			[`option${position}`]: value,
		};
		const matched = product
			? getMatchedVariant(product.variants, updatedOptions)
			: null;

		dispatch({ type: 'SET_VARIANT', payload: matched || null });
		dispatch({ type: 'UPDATE_OPTIONS', payload: updatedOptions });
	};

	const handleStartHaggle = () => {
		dispatch({ type: 'INCREMENT_CHAT_KEY' });
		dispatch({ type: 'TOGGLE_CHAT', payload: true });
		dispatch({ type: 'TOGGLE_MINIMIZE_CHAT', payload: false });
		dispatch({ type: 'TOGGLE_CHAT_PROGRESS', payload: true });
		refetchStore();
	};

	const handleCloseChat = () => {
		dispatch({ type: 'TOGGLE_CHAT', payload: false });
		dispatch({ type: 'TOGGLE_MINIMIZE_CHAT', payload: true });
		dispatch({ type: 'TOGGLE_CHAT_PROGRESS', payload: false });
	};

	const handleToggleMinimize = () => {
		dispatch({ type: 'TOGGLE_MINIMIZE_CHAT', payload: !isChatMinimized });
	};
	useEffect(() => {
		if (productId && storeName) {
			// Check if product is in haggle
			haggleApiService.checkProductInHaggle(productId).then((response) => {
				dispatch({ type: 'SET_HAGGLE_STATUS', payload: response.inHaggle });
				// Set policies if they exist in the response
				if (response.customReturnAndExchangePolicy || response.shippingPolicy) {
					dispatch({
						type: 'SET_POLICIES',
						payload: {
							customReturnAndExchangePolicy:
								response.customReturnAndExchangePolicy,
							shippingPolicy: response.shippingPolicy,
						},
					});
				}
			});
			haggleApiService
				.getProductByIdAndStoreName(storeName, productId, false)
				.then((product) => {
					if (product) {
						dispatch({ type: 'SET_PRODUCT', payload: product });
						dispatch({
							type: 'SET_IMAGE',
							payload: 0,
						});
						dispatch({
							type: 'SET_VARIANT',
							payload: product.variants[0] || null,
						});
						initializeOptionsAndVariant(product);
					}
				});
		}
	}, [productId, storeName]);
	if (!product)
		return (
			<div className="w-full h-[80vh] flex justify-center items-center">
				<FaSpinner size={50} className="animate-spin text-gray-600" />
			</div>
		);

	return (
		<div className="min-h-screen bg-grey-5 p-4">
			<div className="max-w-7xl mx-auto">
				{/* Back Button */}
				<button
					className="glassy-shadow-inset-outward w-10 h-10 rounded-circle flex items-center justify-center text-grey-70 hover:bg-grey-10 transition-all mb-6"
					onClick={() => navigate(-1)}
				>
					<FaChevronLeft className="w-4 h-4" />
				</button>

				{/* Main Container with Relative Positioning */}
				<div className="relative">
					{/* Parent Card - White Background with padding for overlay */}
					<div className="glassy-shadow-inset-outward bg-white rounded-large mt-[10lvh] md:mt-[20lvh] md:pt-0 md:pl-[50%] p-8 min-h-[500px] mb-[50px] md:mb-0">
						<div className="mt-[600px] md:mt-0 md:py-12 space-y-6 px-4">
							{/* Product Title and Price */}
							<div className="space-y-2">
								<h1 className="text-3xl font-bold text-grey-90">
									{product.title}
								</h1>
								<p className="text-3xl font-bold text-grey-90">
									{formatPrice(selectedVariant?.price || 0)}
								</p>
							</div>

							{/* Variant Selection */}
							<div className="space-y-6">
								{product.options.map((option) => (
									<div key={option.id}>
										<div className="flex flex-wrap gap-3 items-center">
											<h3 className="text-lg text-grey-80">{option.name}</h3>
											{option.values.map((value, index) => {
												const isSelected =
													selectedOptions[`option${option.position}`] === value;

												return (
													<button
														key={index}
														className={`
															px-6 py-3 rounded-large font-medium transition-all
															${
																isSelected
																	? 'glassy-shadow-inset-inward text-grey-90 border-2 border-grey-100'
																	: 'glassy-shadow-inset-outward bg-white text-grey-70 hover:bg-grey-10 hover:text-grey-90'
															}
														`}
														onClick={() => {
															selectVariant(value, option.position);
														}}
													>
														{value}
													</button>
												);
											})}
										</div>
									</div>
								))}
							</div>

							{/* Description */}
							{product && product.body_html && (
								<div className="space-y-3">
									<h3 className="text-lg text-grey-80">Description</h3>
									<div
										className="text-grey-60 leading-relaxed"
										dangerouslySetInnerHTML={{ __html: product.body_html }}
									/>
								</div>
							)}

							{/* Policies */}
							{(customReturnAndExchangePolicy || shippingPolicy) && (
								<div className="space-y-4 pt-6 border-t border-grey-20">
									{customReturnAndExchangePolicy && (
										<div>
											<h3 className="font-semibold text-lg text-grey-80 mb-2">
												Return & Exchange Policy
											</h3>
											<p className="text-grey-60 text-sm leading-relaxed">
												{customReturnAndExchangePolicy}
											</p>
										</div>
									)}
									{shippingPolicy && (
										<div>
											<h3 className="font-semibold text-lg text-grey-80 mb-2">
												Shipping Policy
											</h3>
											<p className="text-grey-60 text-sm leading-relaxed">
												{shippingPolicy}
											</p>
										</div>
									)}
								</div>
							)}
						</div>
					</div>

					{/* Images Section - Absolutely Positioned Over Card */}
					<div className="absolute top-[-50px] md:top-[-150px] left-0 md:left-8 w-full md:w-[45%] px-4 md:px-0 pb-10">
						<div className="space-y-6">
							{/* Main Product Image Card */}
							<div className="glassy-shadow-inset-outward bg-white rounded-large p-4">
								<Zoom>
									<img
										src={product.images?.[selectedImage]?.src}
										alt={product.title}
										className="w-full h-[400px] object-contain rounded-base"
									/>
								</Zoom>
							</div>

							{/* Thumbnail Images - Horizontal Scrollable Row */}
							<div className="flex gap-4 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
								{product.images?.map((img, index) => (
									<div
										key={index}
										className="glassy-shadow-inset-outward bg-white rounded-large m-1 flex-shrink-0 w-[150px]"
									>
										<img
											src={img.src}
											alt={img.alt || `Product image ${index}`}
											className={`w-full h-[150px] object-contain rounded-large cursor-pointer hover:scale-105 transition-all ${
												selectedImage === index ? 'ring-2 ring-gray-300' : ''
											}`}
											onClick={() =>
												dispatch({ type: 'SET_IMAGE', payload: index })
											}
										/>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>

			{!isChatInProgress && isProductInHaggle && (
				<button
					onClick={handleStartHaggle}
					className={`fixed bottom-4 left-1/2 transform -translate-x-1/2 md:left-auto md:right-6 md:transform-none w-[90%] md:w-auto md:mx-0 text-white font-semibold py-3 px-10 md:px-[7rem] rounded-lg shadow-lg hover:bg-blue-700 transition-all`}
					style={
						store?.theme
							? { background: getThemeValue(store.theme) }
							: { background: ChatbotThemeGradients[0].value }
					}
				>
					Submit Your Private Offer
				</button>
			)}

			{/* Mobile Chat Container */}
			<div
				className={`md:hidden fixed bottom-0 left-1/2 transform -translate-x-1/2 z-[100] w-[90%] ${
					isChatOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
				}`}
			>
				<div
					className={`glass-gradient-background w-full rounded-lg overflow-hidden ${
						isChatMinimized ? 'h-14' : 'h-[512px]'
					}`}
				>
					<div
						className="w-full h-full overflow-hidden cursor-pointer transition-all duration-500 ease-in-out"
						onClick={!isChatInProgress ? handleStartHaggle : undefined}
					>
						<div
							className={`flex justify-between items-center p-3 text-white `}
							style={
								store?.theme
									? { background: getThemeValue(store.theme) }
									: { background: ChatbotThemeGradients[0].value }
							}
							onClick={handleToggleMinimize}
						>
							<div className="flex justify-center items-center gap-2">
								{store?.businessLogoUrl && (
									<img
										src={store.businessLogoUrl}
										className="w-[32px] h-[32px]"
										alt="Merchant Logo"
									/>
								)}
								<h3 className="font-bold text-lg">Private Room</h3>
							</div>
							<button
								onClick={(e) => {
									if (!isChatMinimized) {
										e.stopPropagation();
										setShowCloseConfirmation(true);
									}
								}}
								className="text-white text-xl"
							>
								✖
							</button>
						</div>

						{isChatInProgress && (
							<div className="h-[90%] overflow-hidden">
								{isChatOpen && isProductInHaggle && selectedVariant && (
									<ChatPage
										key={chatKey}
										productId={productId}
										variantId={selectedVariant.id.toString()}
										selectedVariant={selectedVariant}
										vendorEmail={vendorEmail}
										productOptions={product.options}
										isCSVHaggle={false}
										localCheckout={true}
									/>
								)}

								{showCloseConfirmation && (
									<div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center z-10">
										<p className="text-white text-lg font-semibold mb-4 px-6 text-center">
											Do you want to finish the chat?
										</p>
										<div className="flex gap-4">
											<button
												className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
												onClick={() => {
													handleCloseChat();
													setShowCloseConfirmation(false);
												}}
											>
												Yes
											</button>
											<button
												className="px-4 py-2 bg-gray-300 text-black rounded hover:bg-gray-400"
												onClick={() => setShowCloseConfirmation(false)}
											>
												No
											</button>
										</div>
									</div>
								)}
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Desktop/Tablet Chat Container */}
			<div
				className={`hidden md:block fixed bottom-0 right-6 z-[100] ${
					isChatOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
				}`}
			>
				<ResizableBox
					width={384}
					height={isChatMinimized ? 56 : 512}
					minConstraints={[384, 512]}
					maxConstraints={[700, 700]}
					resizeHandles={isChatMinimized ? [] : ['ne', 'nw']}
					className="glass-gradient-background rounded-lg overflow-hidden"
				>
					<div
						className="w-full h-full overflow-hidden cursor-pointer transition-all duration-500 ease-in-out"
						onClick={!isChatInProgress ? handleStartHaggle : undefined}
					>
						<div
							className={`flex justify-between items-center p-3 text-white `}
							style={
								store?.theme
									? { background: getThemeValue(store.theme) }
									: { background: ChatbotThemeGradients[0].value }
							}
							onClick={handleToggleMinimize}
						>
							<div className="flex justify-center items-center gap-2">
								{store?.businessLogoUrl && (
									<img
										src={store.businessLogoUrl}
										className="w-[32px] h-[32px]"
										alt="Merchant Logo"
									/>
								)}
								<h3 className="font-bold text-lg">Private Room</h3>
							</div>
							<button
								onClick={(e) => {
									if (!isChatMinimized) {
										e.stopPropagation();
										setShowCloseConfirmation(true);
									}
								}}
								className="text-white text-xl"
							>
								✖
							</button>
						</div>

						{isChatInProgress && (
							<div className="h-[90%] overflow-hidden">
								{isChatOpen && isProductInHaggle && selectedVariant && (
									<ChatPage
										key={chatKey}
										productId={productId}
										variantId={selectedVariant.id.toString()}
										selectedVariant={selectedVariant}
										vendorEmail={vendorEmail}
										productOptions={product.options}
										isCSVHaggle={false}
										localCheckout={true}
									/>
								)}

								{showCloseConfirmation && (
									<div className="absolute inset-0 bg-black bg-opacity-60 flex flex-col items-center justify-center z-10">
										<p className="text-white text-lg font-semibold mb-4 px-6 text-center">
											Do you want to finish the chat?
										</p>
										<div className="flex gap-4">
											<button
												className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
												onClick={() => {
													handleCloseChat();
													setShowCloseConfirmation(false);
												}}
											>
												Yes
											</button>
											<button
												className="px-4 py-2 bg-gray-300 text-black rounded hover:bg-gray-400"
												onClick={() => setShowCloseConfirmation(false)}
											>
												No
											</button>
										</div>
									</div>
								)}
							</div>
						)}
					</div>
				</ResizableBox>
			</div>
		</div>
	);
};

export default ShopifyProductDetail;
